'use client';

import { useEffect } from 'react';

/**
 * Registers /sw.js on mount. Mounted once at the root layout level.
 *
 * Why client-only: navigator.serviceWorker only exists in the browser,
 * and we want the SW to register on every page (so a family that lands
 * on /client-portal first still gets push + offline). Mounting at the
 * root means we don't re-register on every route change.
 *
 * Strategy:
 *   - Bail out gracefully on browsers without serviceWorker support
 *   - Skip in dev mode (Next.js HMR + SW caching is a recipe for
 *     stale-asset hell)
 *   - When the SW reports a new version is waiting, fire a one-off
 *     toast asking the user to reload. We don't auto-reload — that's
 *     jarring mid-task — but we make it one tap to refresh.
 */
export default function PWAClient() {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!('serviceWorker' in navigator)) return;
    if (process.env.NODE_ENV !== 'production') return;

    let canceled = false;

    const register = async () => {
      try {
        const reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' });

        // Listen for a new SW taking control after activate. When that
        // happens, the next navigation will pull the new bundle — but
        // the user might be in the middle of a task. Show a soft toast
        // rather than forcing a reload.
        reg.addEventListener('updatefound', () => {
          const newWorker = reg.installing;
          if (!newWorker) return;
          newWorker.addEventListener('statechange', () => {
            if (canceled) return;
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              // Only show "update available" if there's already a
              // controller — first install doesn't need it.
              showUpdateToast();
            }
          });
        });
      } catch (err) {
        // SW registration failing isn't fatal — the app works fine
        // online without it. Log and move on.
        console.warn('[PWAClient] SW registration failed:', err);
      }
    };

    register();
    return () => {
      canceled = true;
    };
  }, []);

  return null;
}

function showUpdateToast() {
  // Never interrupt an auth flow: a reload mid-password-reset / mid-pair
  // discards the one-time token in the URL and breaks the link. The new SW
  // still takes over on the user's next navigation — we just don't prompt here.
  //
  // Also NEVER show it anywhere in the Summer Institute kiosk experience
  // (student iPads, family portal, pairing): a child will tap "Reload", and
  // offline that reloads into a dead "cannot open page", while online it can
  // bounce to the studio login. The SW auto-updates silently (skipWaiting +
  // clients.claim), so the kiosk gets fresh code on its next relaunch with no
  // button to tap.
  const p = window.location.pathname;
  if (
    p.startsWith('/auth') ||
    p === '/login' ||
    p.startsWith('/summer-institute') ||
    p === '/pair' ||
    p.startsWith('/p/')
  )
    return;
  // Idempotent: don't stack toasts if multiple updatefound events fire.
  if (document.getElementById('cfva-sw-update-toast')) return;

  const toast = document.createElement('div');
  toast.id = 'cfva-sw-update-toast';
  toast.style.cssText = [
    'position:fixed',
    'bottom:16px',
    'left:50%',
    'transform:translateX(-50%)',
    'background:#0f766e',
    'color:white',
    'padding:12px 16px',
    'border-radius:12px',
    'box-shadow:0 8px 24px rgba(0,0,0,0.25)',
    'display:flex',
    'gap:12px',
    'align-items:center',
    'font:500 14px/1.4 system-ui,-apple-system,Segoe UI,sans-serif',
    'z-index:9999',
    'max-width:calc(100vw - 32px)',
  ].join(';');
  toast.innerHTML = `
    <span>A newer version is ready.</span>
    <button id="cfva-sw-update-reload" style="background:white;color:#0f766e;border:none;padding:6px 12px;border-radius:8px;font:600 13px system-ui;cursor:pointer;">Reload</button>
    <button id="cfva-sw-update-dismiss" aria-label="Dismiss" style="background:transparent;color:white;border:none;padding:4px 8px;border-radius:6px;cursor:pointer;opacity:0.8;font:600 16px system-ui;">×</button>
  `;
  document.body.appendChild(toast);

  document.getElementById('cfva-sw-update-reload')?.addEventListener('click', () => {
    window.location.reload();
  });
  document.getElementById('cfva-sw-update-dismiss')?.addEventListener('click', () => {
    toast.remove();
  });
}
